import { Localized, useLocalization } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
    Box,
    Skeleton,
    Stack,
    Avatar,
    Autocomplete,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes";
import { SaveButton } from "../../../../app/components/SaveButton";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { RequiredError } from "../../../../app/components/RequiredError";
import { CancelButton } from "../../../../app/components/CancelButton";
import { DocumentLanguages, DocumentTypes } from "../../../documents/documentTypes";
import { DocumentLanguageSelect } from "../../../documents/controls/DocumentLanguageSelect";
import { alpha } from "@mui/material/styles";
import { CreationMode, DocumentToCreate, prepareValidationStage, PrepareValidationStagePayload, ProcessStageOverview } from "../validationProcessApi";
import { ClientDocumentTemplateSelectField } from "../../../documents/templates/ClientDocumentTemplateSelectField";
import { LoadUsers } from "../../../../app/AppDataLoader";
import PersonIcon from "@mui/icons-material/Person";
import { selectAllUsers, User } from "../../../users/usersSlice";
import React from "react";
import { buildDocumentNumberByProcess, buildDocumentTitleByProcess } from "../utils/documentNumberingHelper";
import { selectDeviceById } from "../../../device/deviceSlice";
import DocumentTargetInfo from "./DocumentTargetInfo";
import { DocumentNumberTextField } from "../../../documents/controls/DocumentNumberTextField";

type UserOption = {
    userName: string;
    firstname: string;
    surname: string;
    email: string;
    position?: string;
};


export interface DQStageActivationProps {
    processId: AppId | null;
    structureId: number;
    processName?: string;
    processNumber?: string;
    deviceId?: string;

    onSuccess: (stage: ProcessStageOverview) => void;
    onCancel: () => void;
}

export const DQStageActivation: React.FC<DQStageActivationProps> = (props) => {
    const fixedDocType: DocumentTypes = "DQ";
    const stageCode = "DQ";

    const dispatch = useAppDispatch();
    const { l10n } = useLocalization()

    const allUsers = useAppSelector(selectAllUsers);

    const [language, setLanguage] = useState<DocumentLanguages | "">("");

    const [documentNumber, setDocumentNumber] = useState("");
    const [docNoHasError, setDocNoHasError] = useState(false);
    const [title, setTitle] = useState("");
    const [responsible, setResponsible] = useState<User | null>(null);

    const [responsibleDirty, setResponsibleDirty] = useState(false);

    const [creationMode, setCreationMode] = useState<CreationMode>("TEMPLATE");
    const [templateId, setTemplateId] = useState<string | "">("");

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const device = useAppSelector((state) =>
        props.deviceId ? selectDeviceById(state, props.deviceId) : null
    );

    const [numberDirty, setNumberDirty] = useState(false);
    const [titleDirty, setTitleDirty] = useState(false);

    const isResponsibleOk = !!(responsible && responsible.userName !== "");

    const isBasicsOk =
        !!documentNumber.trim() && !docNoHasError &&
        !!title.trim() &&
        !!language &&
        !!props.processId &&
        isResponsibleOk;

    const isTemplateOk =
        creationMode === "EMPTY"
            ? true
            : (templateId !== "" && Number(templateId) > 0);

    const isFormValid = isBasicsOk && isTemplateOk;

    useEffect(() => {
        if (!numberDirty) {
            setDocumentNumber(buildDocumentNumberByProcess(props.processNumber, String(fixedDocType)));
        }
        if (!titleDirty) {

            if (language) {
                setTitle(buildDocumentTitleByProcess(language, String(fixedDocType), device ?? undefined));
            } else {
                setTitle("");
            }
        }
    }, [fixedDocType, language, device?.name, device?.serialNo]);

    // --- handlery
    const handleTitleChange: AppTextFieldHandler = useCallback((e) => {
        setTitle(e.target.value);
        setTitleDirty(true);
    }, []);

    const handleDocumentNumberChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setDocumentNumber(e.target.value);
        setNumberDirty(true);
    };

    const handleModeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCreationMode(e.target.value as CreationMode);
    }, []);

    const handleLanguageChange = useCallback((language: DocumentLanguages | "") => {
        if (language) setLanguage(language);
    }, []);

    const handleSaveClick = async () => {
        if (!isFormValid) {
            setServerError("fill-all-required-fields");
            setResponsibleDirty(true);
            return;
        }

        const normalizedTemplateId =
            creationMode === "TEMPLATE" ? Number(templateId) : 0;

        const docs: DocumentToCreate[] = [
            {
                type: fixedDocType,
                number: documentNumber.trim(),
                title: title.trim(),
                language,
                responsible: responsible?.userName || null,
                creationMode: creationMode,
                templateId: normalizedTemplateId,
            },
        ];

        const payload: PrepareValidationStagePayload = {
            stageCode: stageCode,
            processId: props.processId!,
            documents: docs,
            toLink: []
        };

        setSaving(true);
        setServerError("");
        try {

            const result = await dispatch(prepareValidationStage(payload));
            if (prepareValidationStage.fulfilled.match(result)) {
                const stage = result.payload as ProcessStageOverview;
                dispatch(showSuccess("saved"));
                props.onSuccess(stage as ProcessStageOverview);
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title
                    }
                }
                setServerError(errorMsg)
            }

        } catch (e) {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open fullWidth maxWidth="md"

            slotProps={{
                backdrop: {
                    sx: {
                        bgcolor: (t) => alpha(t.palette.grey[900], 0.5),
                    },
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                <Localized id="process-dq-stage-activation-title">
                    <span>Przygotowanie dokumentu DQ</span>
                </Localized>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <DocumentTargetInfo
                    objectId={props.deviceId}
                    objectType="DEVICE"
                    processNumber={props.processNumber}
                    processTitle={props.processName}
                    structureId={props.structureId}
                />

                {/* 1) Numer + Język (język obok numeru) */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                    <Grid item xs={12} md={8}>
                        <DocumentNumberTextField
                            fullWidth={true}
                            required={true}
                            value={documentNumber}
                            onChange={handleDocumentNumberChange}
                            label={<Localized id="document-number"><span>_Numer dokumentu</span></Localized>}
                            documentType={fixedDocType}
                            onErrorChange={(err) => setDocNoHasError(err)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>

                        <DocumentLanguageSelect
                            defaultValue={language}
                            required
                            onChange={handleLanguageChange} />

                    </Grid>
                </Grid>

                {/* 2) Tytuł (pod numerem) */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField
                            helperText={title === "" && <RequiredError />}
                            required
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={title}
                            error={title === ""}
                            onChange={handleTitleChange}
                            label={<Localized id="document-title"><span>_Tytuł</span></Localized>}
                        />
                    </Grid>
                </Grid>

                {/* 3) Osoba odpowiedzialna (pod tytułem) */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                    <Grid item xs={12}>
                        <LoadUsers component={<Skeleton variant="rectangular" height={180} />}>
                            <Autocomplete
                                options={allUsers}
                                onChange={(_, u) => {
                                    setResponsible(u ?? null);
                                    setResponsibleDirty(true);
                                }}
                                onBlur={() => setResponsibleDirty(true)}
                                value={responsible}
                                isOptionEqualToValue={(o, v) => o.userName === v.userName}
                                getOptionLabel={(u) => (u ? `${u.firstname} ${u.surname} (${u.email})` : "")}
                                renderOption={(props, u) => (
                                    <li {...props} key={u.userName}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar
                                                sx={{ width: 28, height: 28, fontSize: 12, lineHeight: 1 }}
                                                src={(u as any).avatarUrl}
                                            >
                                                {<PersonIcon sx={{ fontSize: 16 }} />}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {u.surname} {u.firstname}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {u.email}{u.position ? ` · ${u.position}` : ""}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params}
                                        label={<Localized id="document-responsible-person-for-preparing"><span>_responsible</span></Localized>}
                                        placeholder={l10n.getString('choose-person', null, '_choose-persone')}
                                        required
                                        error={responsibleDirty && !isResponsibleOk}
                                        helperText={responsibleDirty && !isResponsibleOk ? <RequiredError /> : null} />
                                )}
                            />
                        </LoadUsers>
                    </Grid>
                </Grid>

                {/* 4) Tryb tworzenia (na końcu) */}
                <Box
                    sx={(t) => ({
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 1,
                        p: 1.25,
                        mt: 1,
                    })}
                >
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                        <Localized id="document-creation-mode"><span>_Tryb tworzenia</span></Localized>
                    </Typography>
                    <RadioGroup row value={creationMode} onChange={handleModeChange}>
                        <FormControlLabel
                            value="EMPTY"
                            control={<Radio size="small" />}
                            sx={{ mr: 2 }}
                            label={<Typography variant="body2">
                                <Localized id="document-creation-mode-empty"><span>_Pusty</span></Localized>
                            </Typography>}
                        />
                        <FormControlLabel
                            value="TEMPLATE"
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">
                                <Localized id="document-creation-mode-template"><span>_template</span></Localized>
                            </Typography>}
                        />
                    </RadioGroup>

                    {creationMode === "TEMPLATE" && (
                        <Box sx={{ mt: 1 }}>
                            <ClientDocumentTemplateSelectField
                                structureId={props.structureId}
                                documentType={fixedDocType}
                                value={templateId}
                                onChange={(id) => setTemplateId(id)}
                            />
                        </Box>
                    )}
                </Box>

                {serverError && (
                    <Alert sx={{
                        mt: 2,
                        position: 'sticky', bottom: 0, m: 0, borderRadius: 0,

                    }} onClose={() => setServerError("")} severity="error">
                        <Localized id={serverError}><span>Server error</span></Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={props.onCancel} />
            </DialogActions>
        </Dialog >
    );
};
